import { format, parseISO, differenceInHours } from 'date-fns';
import {
  NEW_FILE_HOURS_THRESHOLD,
  PROJECT_FILES_MAX_BYTES,
  PROJECT_FILES_MAX_FILENAME_LENGTH,
  PROJECT_FILES_MAX_SIZE_LABEL,
} from './projectFilesPopoverConstants';
import type { EntityFilesPopoverRow } from './entityFilesPopover.types';

import { formatAuthorSheetsOneDecimal } from '@/lib/projectFileMetrics/authorSheetsCalculator';

/** Свежие файлы выше: по display_order, затем по дате загрузки. */
export function sortProjectFilesNewestFirst<T extends EntityFilesPopoverRow>(files: T[]): T[] {
  return [...files].sort((a, b) => {
    if (b.display_order !== a.display_order) return b.display_order - a.display_order;
    const ta = new Date(a.uploaded_at).getTime();
    const tb = new Date(b.uploaded_at).getTime();
    if (tb !== ta) return tb - ta;
    return b.file_id.localeCompare(a.file_id);
  });
}

export function dataTransferHasFiles(dt: DataTransfer | null | undefined): boolean {
  if (!dt?.types?.length) return false;
  for (let i = 0; i < dt.types.length; i++) {
    if (dt.types[i] === 'Files') return true;
  }
  return false;
}

export function formatFileDate(iso: string): string {
  try {
    return format(parseISO(iso), 'dd.MM.yyyy HH:mm');
  } catch {
    return iso;
  }
}

export function formatFileSize(bytes: number | null): string | null {
  if (bytes === null || bytes === undefined) return null;
  const sizes = ['Б', 'КБ', 'МБ', 'ГБ'];
  if (bytes === 0) return '0 Б';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  if (i === 0) return `${bytes} ${sizes[i]}`;
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
}

export function isRecentlyUploaded(uploadedAt: string): boolean {
  try {
    return differenceInHours(new Date(), parseISO(uploadedAt)) < NEW_FILE_HOURS_THRESHOLD;
  } catch {
    return false;
  }
}

function getFileExtension(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() ?? '';
}

export function shouldShowProjectFileMetrics(fileName: string): boolean {
  const ext = getFileExtension(fileName);
  return ext === 'pdf' || ext === 'docx';
}

function normalizeAuthorSheetsDisplay(value: number | string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(n)) return null;
  return formatAuthorSheetsOneDecimal(n);
}

/**
 * Хвост строки файла: для PDF/DOCX — «N АЛ • N СТР • N МБ», иначе только размер.
 */
export function formatProjectFileMetricsTrailing(args: {
  fileName: string;
  fileSize: number | null;
  authorSheets?: number | string | null;
  pages?: number | null;
  /** PDF/DOCX, расчёт не удался — N/A для АЛ и СТР */
  metricsUnavailable?: boolean;
}): string | null {
  const sizeStr = formatFileSize(args.fileSize);
  if (!shouldShowProjectFileMetrics(args.fileName)) {
    return sizeStr;
  }
  const al =
    args.metricsUnavailable ? 'N/A' : normalizeAuthorSheetsDisplay(args.authorSheets) ?? 'N/A';
  const st =
    args.metricsUnavailable
      ? 'N/A'
      : args.pages != null && Number.isFinite(Number(args.pages))
        ? String(Math.max(0, Math.round(Number(args.pages))))
        : 'N/A';
  if (!sizeStr) return `${al} АЛ • ${st} СТР`;
  return `${al} АЛ • ${st} СТР • ${sizeStr}`;
}

/**
 * Последний сегмент через точку — неизменяемое расширение (.pdf на конце).
 * Скрытые имена вида `.env` считаются целиком «основанием» без отделяемого расширения.
 */
export function splitFileNameStemAndSuffix(fileName: string): { stem: string; suffix: string } {
  const name = fileName;
  const i = name.lastIndexOf('.');
  if (i <= 0 || i >= name.length - 1) {
    return { stem: fileName, suffix: '' };
  }
  return { stem: name.slice(0, i), suffix: name.slice(i) };
}

const AUDIO_EXTENSIONS = new Set([
  'mp3',
  'wav',
  'flac',
  'ogg',
  'aac',
  'm4a',
  'opus',
]);

/** Расширение по имени файла — для плеера в попапе (MIME с сервера в типе строки нет). */
export function isAudioFileName(fileName: string): boolean {
  const ext = getFileExtension(fileName);
  return AUDIO_EXTENSIONS.has(ext);
}

/** Длительность для подписи плеера: `m:ss` или `h:mm:ss` при длине ≥ 1 ч. */
export function formatAudioClock(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '0:00';
  const s = Math.floor(totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad2 = (n: number) => String(n).padStart(2, '0');
  if (h > 0) return `${h}:${pad2(m)}:${pad2(sec)}`;
  return `${m}:${pad2(sec)}`;
}

export function getFileTypeLabel(fileName: string): string {
  const ext = getFileExtension(fileName);
  const map: Record<string, string> = {
    pdf: 'PDF', doc: 'DOC', docx: 'DOCX', rtf: 'RTF',
    mp3: 'MP3', wav: 'WAV', flac: 'FLAC', ogg: 'OGG', aac: 'AAC', m4a: 'M4A', opus: 'OPUS',
    txt: 'TXT', md: 'MD',
    zip: 'ZIP', rar: 'RAR', '7z': '7Z',
    jpg: 'JPG', jpeg: 'JPG', png: 'PNG', gif: 'GIF', webp: 'WEBP',
    mp4: 'MP4', avi: 'AVI', mkv: 'MKV', mov: 'MOV',
    xlsx: 'XLSX', xls: 'XLS', csv: 'CSV',
    ppt: 'PPT', pptx: 'PPTX',
  };
  return map[ext] ?? (ext.toUpperCase() || 'FILE');
}

export function validateFilesForDraft(selectedFiles: File[], showToastError: (msg: string) => void): boolean {
  for (const file of selectedFiles) {
    if (file.size > PROJECT_FILES_MAX_BYTES) {
      showToastError(
        `Файл «${file.name}» слишком большой. Максимальный размер: ${PROJECT_FILES_MAX_SIZE_LABEL}.`
      );
      return false;
    }
    if (file.name.length > PROJECT_FILES_MAX_FILENAME_LENGTH) {
      showToastError(
        `Название файла «${file.name}» слишком длинное. Максимум ${PROJECT_FILES_MAX_FILENAME_LENGTH} символов.`
      );
      return false;
    }
  }
  return true;
}
